// Module-level media-upload session — survives MediaUploader RE-MOUNTS/re-renders so a long
// (GB) video upload keeps a live UI + a reliable "don't reload" guard even when React resets the
// component. The gate this closes (co-drive, stg-fix2-1): a background re-render/remount reset the
// component's queue + busy → the progress panel vanished AND the #GR9f beforeunload guard (which
// was keyed off the component's `busy`) silently dropped → a later reload killed the in-flight tus
// upload before media-create → nothing catalogued. Holding the queue + the in-flight count HERE
// (module scope, one source of truth via useSyncExternalStore) means a remount re-attaches to the
// running session instead of losing it, and the reload guard can never be falsely absent.

export type UploadStatus = 'queued' | 'uploading' | 'cataloguing' | 'finalizing' | 'done' | 'error';

export interface UploadItem {
  key: string;
  name: string;
  kind: 'image' | 'video';
  status: UploadStatus;
  detail?: string;
  /** Live upload % (0–100) for the in-flight file — tus onProgress feeds it (video). */
  progress?: number;
}

interface SessionState {
  clientId: string | null;
  items: UploadItem[];
  busy: boolean;
}

let state: SessionState = { clientId: null, items: [], busy: false };
const listeners = new Set<() => void>();

// The actual File (+ kind + target gallery) per item key — module-level so a component remount
// doesn't lose "Retry failed". Files aren't React state; the in-flight promise holds its own File.
const ctx = new Map<string, { file: File; kind: 'image' | 'video'; groupId: string }>();

// In-flight upload count — the reload-guard truth, INDEPENDENT of any component's `busy` (which
// resets on remount). A remount can never make this falsely 0 while an upload is running.
let inFlight = 0;

function emit(): void {
  for (const l of listeners) l();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stable reference between changes — required by useSyncExternalStore (we replace `state` only on
 *  a real change, so React re-renders exactly when it must). */
export function getSnapshot(): SessionState {
  return state;
}

/**
 * Attach to (or start) the session for a client. Returning to the SAME client re-attaches to an
 * in-flight session (survives a remount → the panel rehydrates); switching to a DIFFERENT client
 * clears the UI for the new one (any prior uploads keep running via their own promises). No-op when
 * already on this client — so a remount with the same client KEEPS its items (the whole point).
 */
export function ensureSession(clientId: string): void {
  if (state.clientId !== clientId) {
    state = { clientId, items: [], busy: false };
    emit();
  }
}

export function setItems(fn: (items: UploadItem[]) => UploadItem[]): void {
  state = { ...state, items: fn(state.items) };
  emit();
}

export function patchItem(key: string, patch: Partial<UploadItem>): void {
  setItems((items) => items.map((it) => (it.key === key ? { ...it, ...patch } : it)));
}

export function setBusy(busy: boolean): void {
  if (state.busy === busy) return;
  state = { ...state, busy };
  emit();
}

export const uploadCtx = ctx;

export function beginInFlight(): void {
  inFlight += 1;
}
export function endInFlight(): void {
  inFlight = Math.max(0, inFlight - 1);
}
export function uploadsInFlight(): number {
  return inFlight;
}

// ONE global reload guard, installed once at module load — warns whenever ANY upload is in flight,
// regardless of which (or whether a) MediaUploader is mounted. A remount resets a component's
// `busy`; it can never reset THIS, so the "don't reload mid-upload" warning is no longer silently
// lost (the (b)(iii) gap). ⚠️ Browser limit: after a hard reload the File object is gone, so tus
// can't SILENTLY auto-resume — this guard (warn-before-unload) is the primary reload defense;
// storeFingerprintForResuming persists the tus offset so a re-selected file resumes, not restarts.
if (typeof window !== 'undefined') {
  const w = window as unknown as { __alchmyUploadGuard?: boolean };
  if (!w.__alchmyUploadGuard) {
    w.__alchmyUploadGuard = true;
    window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
      if (inFlight > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    });
  }
}
